import React, { useState, useMemo } from 'react';
import { FeedbackDisplayProps, WordAnalysis, WordStatus, AIAnalysisResponse } from '../types';
import { analyzeResults } from '../services/api';
import { calculateLevenshteinDistance, getMatchQuality } from '../utils/levenshtein';

const FeedbackDisplay: React.FC<FeedbackDisplayProps> = ({
  targetText,
  transcriptionResult,
  isPlaying,
  onPlayRecording,
  onStopPlayback,
  hasRecording,
}) => {
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysisResponse | null>(null);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Analyze words using Levenshtein distance
  const { analyzedWords, stats, overallScore } = useMemo(() => {
    if (!transcriptionResult) {
      return { analyzedWords: [], stats: { correct: 0, unclear: 0, substitutions: 0, omissions: 0, insertions: 0, total: 0 }, overallScore: 0 };
    }

    const targetWords = targetText.split(/\s+/).filter(w => w.length > 0);
    const transcribedWords = transcriptionResult.words || [];

    // Use Levenshtein algorithm to align words
    const levenshteinResult = calculateLevenshteinDistance(targetWords, transcribedWords);

    // Convert diffs to WordAnalysis format
    const analyzed: WordAnalysis[] = levenshteinResult.diffs.map(diff => {
      if (diff.operation === 'match') {
        const quality = getMatchQuality(diff.confidence);
        return {
          word: diff.targetWord!,
          status: quality as WordStatus,
          confidence: diff.confidence,
          transcribed: diff.transcribedWord!
        };
      }

      if (diff.operation === 'substitution') {
        return {
          word: diff.targetWord!,
          status: 'substitution' as WordStatus,
          confidence: diff.confidence,
          transcribed: diff.transcribedWord!
        };
      }

      if (diff.operation === 'omission') {
        return {
          word: diff.targetWord!,
          status: 'omission' as WordStatus,
          confidence: 0,
          transcribed: undefined
        };
      }

      // insertion
      return {
        word: diff.transcribedWord!,
        status: 'insertion' as WordStatus,
        confidence: diff.confidence,
        transcribed: diff.transcribedWord!
      };
    });

    const statsCalc = {
      correct: analyzed.filter(w => w.status === 'correct').length,
      unclear: analyzed.filter(w => w.status === 'unclear').length,
      substitutions: analyzed.filter(w => w.status === 'substitution').length,
      omissions: analyzed.filter(w => w.status === 'omission').length,
      insertions: analyzed.filter(w => w.status === 'insertion').length,
      total: targetWords.length // Score based on target words count
    };

    const score = statsCalc.total > 0
      ? Math.round((statsCalc.correct / statsCalc.total) * 100)
      : 0;

    return { analyzedWords: analyzed, stats: statsCalc, overallScore: score };
  }, [targetText, transcriptionResult]);

  if (!transcriptionResult) return null;

  const getWordStyles = (status: WordStatus): string => {
    switch (status) {
      case 'correct':
        return 'bg-green-200 dark:bg-green-900/50 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700';
      case 'unclear':
        return 'bg-yellow-200 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-200 border-yellow-300 dark:border-yellow-700';
      case 'substitution':
        return 'bg-red-200 dark:bg-red-900/50 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700';
      case 'omission':
        return 'bg-red-200 dark:bg-red-900/50 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700 opacity-60';
      case 'insertion':
        return 'bg-purple-200 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 border-purple-300 dark:border-purple-700';
      default:
        return 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600';
    }
  };

  const getStatusLabel = (status: WordStatus): string => {
    switch (status) {
      case 'correct':
        return 'Correct';
      case 'unclear':
        return 'Unclear';
      case 'substitution':
        return 'Substitution';
      case 'omission':
        return 'Omitted (skipped)';
      case 'insertion':
        return 'Extra word';
      default:
        return '';
    }
  };

  const handleGetAIFeedback = async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const result = await analyzeResults({
        targetText,
        transcribedText: transcriptionResult.text,
        words: analyzedWords.map(w => ({
          word: w.word,
          status: w.status,
          confidence: w.confidence,
          transcribed: w.transcribed
        })),
        overallScore
      });
      setAiAnalysis(result);
    } catch (err) {
      console.error('AI analysis error:', err);
      setAiError('Failed to get AI feedback. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="mt-8 p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Your Results</h3>
        {hasRecording && onPlayRecording && onStopPlayback && (
          <button
            onClick={isPlaying ? onStopPlayback : onPlayRecording}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              isPlaying
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
            }`}
          >
            {isPlaying ? (
              <>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
                Stop
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
                Listen to Yourself
              </>
            )}
          </button>
        )}
      </div>

      {/* Overall Score */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="text-gray-600 dark:text-gray-300">Overall Score:</span>
          <span className={`text-2xl font-bold ${
            overallScore >= 80 ? 'text-green-600 dark:text-green-400' :
            overallScore >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'
          }`}>
            {overallScore}%
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-4 text-sm">
          <span className="text-green-600 dark:text-green-400">{stats.correct} correct</span>
          <span className="text-yellow-600 dark:text-yellow-400">{stats.unclear} unclear</span>
          <span className="text-red-600 dark:text-red-400">{stats.substitutions} substitutions</span>
          <span className="text-red-600 dark:text-red-400">{stats.omissions} omissions</span>
          <span className="text-purple-600 dark:text-purple-400">{stats.insertions} insertions</span>
        </div>
      </div>

      {/* Word by word analysis */}
      <div className="flex flex-wrap gap-2">
        {analyzedWords.map((item, index) => (
          <div
            key={index}
            className={`
              px-3 py-2 rounded-lg border-2 transition-all
              ${getWordStyles(item.status)}
              group relative cursor-help
            `}
          >
            <span className="font-medium">{item.word}</span>
            {/* Show confidence for correct/unclear words */}
            {(item.status === 'correct' || item.status === 'unclear') && (
              <span className="ml-2 text-xs opacity-70">
                {Math.round(item.confidence * 100)}%
              </span>
            )}
            {/* Show what was heard for substitutions */}
            {item.status === 'substitution' && item.transcribed && (
              <span className="ml-2 text-xs opacity-70">
                → {item.transcribed}
              </span>
            )}
            {/* Show skipped indicator for omissions */}
            {item.status === 'omission' && (
              <span className="ml-2 text-xs opacity-70">
                (skipped)
              </span>
            )}
            {/* Show extra indicator for insertions */}
            {item.status === 'insertion' && (
              <span className="ml-2 text-xs opacity-70">
                (extra)
              </span>
            )}

            {/* Tooltip */}
            <div className="
              absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
              bg-gray-900 text-white text-xs rounded py-1 px-2
              opacity-0 group-hover:opacity-100 transition-opacity
              whitespace-nowrap pointer-events-none z-10
            ">
              <div>{getStatusLabel(item.status)}</div>
              {item.transcribed && item.status !== 'correct' && (
                <div>Heard: "{item.transcribed}"</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Transcribed text */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">What we heard:</p>
        <p className="text-gray-700 dark:text-gray-200 italic">"{transcriptionResult.text}"</p>
      </div>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-3 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 bg-green-200 dark:bg-green-900/50 rounded border border-green-300 dark:border-green-700"></span>
          <span className="text-gray-600 dark:text-gray-300">Correct</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 bg-yellow-200 dark:bg-yellow-900/50 rounded border border-yellow-300 dark:border-yellow-700"></span>
          <span className="text-gray-600 dark:text-gray-300">Unclear</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 bg-red-200 dark:bg-red-900/50 rounded border border-red-300 dark:border-red-700"></span>
          <span className="text-gray-600 dark:text-gray-300">Substitution</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 bg-red-200 dark:bg-red-900/50 rounded border border-red-300 dark:border-red-700 opacity-60"></span>
          <span className="text-gray-600 dark:text-gray-300">Omission</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 bg-purple-200 dark:bg-purple-900/50 rounded border border-purple-300 dark:border-purple-700"></span>
          <span className="text-gray-600 dark:text-gray-300">Insertion</span>
        </div>
      </div>

      {/* AI Feedback Section */}
      <div className="mt-6 border-t dark:border-gray-700 pt-6">
        {!aiAnalysis && !aiLoading && (
          <button
            onClick={handleGetAIFeedback}
            className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Get AI Feedback
          </button>
        )}

        {aiLoading && (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mb-3"></div>
            <p className="text-gray-600 dark:text-gray-300">Getting AI feedback...</p>
          </div>
        )}

        {aiError && (
          <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
            <p className="text-red-700 dark:text-red-300">{aiError}</p>
            <button
              onClick={handleGetAIFeedback}
              className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline"
            >
              Try again
            </button>
          </div>
        )}

        {aiAnalysis && (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
              <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              AI Coach Feedback
            </h4>

            {/* Summary */}
            <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
              <p className="text-gray-700 dark:text-gray-200">{aiAnalysis.summary}</p>
            </div>

            {/* Strengths */}
            {aiAnalysis.strengths.length > 0 && (
              <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
                <h5 className="font-semibold text-green-800 dark:text-green-300 mb-2">Strengths</h5>
                <ul className="space-y-1">
                  {aiAnalysis.strengths.map((strength, index) => (
                    <li key={index} className="text-green-700 dark:text-green-400 flex items-start gap-2">
                      <span className="text-green-500 dark:text-green-400">•</span>
                      <span>{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Areas to Improve */}
            {aiAnalysis.areasToImprove.length > 0 && (
              <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-4">
                <h5 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">Areas to Improve</h5>
                <ul className="space-y-1">
                  {aiAnalysis.areasToImprove.map((area, index) => (
                    <li key={index} className="text-yellow-700 dark:text-yellow-400 flex items-start gap-2">
                      <span className="text-yellow-500 dark:text-yellow-400">•</span>
                      <span>{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tips */}
            {aiAnalysis.tips.length > 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
                <h5 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Practice Tips</h5>
                <ul className="space-y-1">
                  {aiAnalysis.tips.map((tip, index) => (
                    <li key={index} className="text-blue-700 dark:text-blue-400 flex items-start gap-2">
                      <span className="text-blue-500 dark:text-blue-400">{index + 1}.</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Encouragement */}
            <div className="bg-pink-50 dark:bg-pink-900/30 rounded-lg p-4">
              <p className="text-pink-700 dark:text-pink-300 italic text-center">{aiAnalysis.encouragement}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackDisplay;
